/** Wrapper assets and their siblings, indexed from the CMS once per session. */

import { Assets, EntityType } from 'trace-viewer/cms';
import { assetFileUrl } from 'trace-viewer/content';
import type { WrapperAssetKey, WrapperLibrary } from 'trace-viewer/editor';
import { apiConfig } from '../catalog-api';
import { API_BASE_URL, ensureFreshToken } from '../catalog-auth';

function metadataKey(raw: unknown): WrapperAssetKey | null {
    let value: unknown = raw;
    if (typeof value === 'string') {
        try { value = JSON.parse(value); } catch { return null; }
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const { family, size, set } = value as Record<string, unknown>;
    if (typeof family !== 'string' || !family.trim()
        || typeof size !== 'string' || !size.trim()
        || typeof set !== 'string' || !set.trim()) return null;
    return { family, size, set };
}

const keyId = ({ family, size, set }: WrapperAssetKey): string => JSON.stringify([family, size, set]);

async function loadLibrary(): Promise<WrapperLibrary> {
    const token = await ensureFreshToken();
    if (!token) throw new Error('Not logged into catalog. Please sign in via the Catalog panel first.');
    const { data } = await new Assets(apiConfig(token)).assetsList({ Kind: EntityType.Wrapper });
    const byUrl = new Map<string, WrapperAssetKey>();
    const byKey = new Map<string, string>();
    for (const row of data ?? []) {
        if (row.entityType !== EntityType.Wrapper || !row.assetId
            || !row.originalFilename?.toLowerCase().endsWith('.gltf')) continue;
        const key = metadataKey(row.metadata);
        if (!key) continue;
        const url = assetFileUrl(API_BASE_URL, row.assetId);
        byUrl.set(url, key);
        if (!byKey.has(keyId(key))) byKey.set(keyId(key), url);
    }
    return {
        describe: assetUrl => byUrl.get(assetUrl) ?? null,
        find: key => byKey.get(keyId(key)) ?? null,
    };
}

let cached: Promise<WrapperLibrary> | null = null;

/** Cache one listing for the whole session; concurrent callers share it and failed loads can be retried. */
export function getWrapperLibrary(): Promise<WrapperLibrary> {
    if (!cached) {
        cached = loadLibrary().catch(error => {
            cached = null;
            throw error;
        });
    }
    return cached;
}
